import { Link, useNavigate } from "react-router-dom";

import MeridianLockup from "../components/MeridianLockup";
import { signOut, useAuth } from "../lib/auth";

/**
 * MER-169: branded 404. Shows MeridianLockup + helpful copy + two
 * CTAs (back to dashboard, sign out) on the radial-gradient card
 * surface. `Back to dashboard` honours the signed-in slug if we have
 * one; otherwise it routes to /login.
 */
export default function NotFound() {
  const navigate = useNavigate();
  const { session } = useAuth();

  // Pull the customer slug from the session metadata if present. Falls
  // back to "/" which AuthGate routes to login or to the first tenant.
  const slug =
    (session?.user.user_metadata as { customer_slug?: string } | undefined)?.customer_slug ?? null;
  const backHref = slug ? `/${slug}` : "/login";

  async function handleSignOut() {
    await signOut();
    navigate("/login", { replace: true });
  }

  return (
    <main
      style={{
        display: "grid",
        placeItems: "center",
        minHeight: "100vh",
        padding: "32px",
        background:
          "radial-gradient(at 30% 20%, rgba(38,168,159,0.06), transparent 50%), var(--bg)",
      }}
    >
      <div
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: 16,
          padding: "36px 36px 28px",
          width: "100%",
          maxWidth: 420,
          boxShadow: "var(--shadow-card)",
          display: "grid",
          gap: 18,
          textAlign: "center",
        }}
      >
        <div style={{ display: "grid", justifyItems: "center", gap: 14 }}>
          <MeridianLockup size="lg" align="stack" />
        </div>

        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ fontSize: 48, fontWeight: 800, color: "var(--brand-deep)" }}>404</div>
          <p style={{ fontSize: 14, color: "var(--text-2)" }}>
            That page doesn&apos;t exist or you don&apos;t have access to it.
          </p>
        </div>

        <div style={{ display: "grid", gap: 10 }}>
          <Link to={backHref} style={primaryButtonStyle}>
            Back to dashboard
          </Link>
          {session ? (
            <button type="button" onClick={handleSignOut} style={secondaryButtonStyle}>
              Sign out
            </button>
          ) : null}
        </div>

        <div
          style={{
            borderTop: "1px solid var(--border-subtle)",
            marginTop: 6,
            paddingTop: 12,
          }}
        >
          <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Need help?{" "}
            <a
              href="mailto:support@meridiananalytics.io"
              style={{ color: "var(--brand-deep)", textDecoration: "none" }}
            >
              support@meridiananalytics.io
            </a>
          </p>
        </div>
      </div>
    </main>
  );
}

const primaryButtonStyle: React.CSSProperties = {
  font: "inherit",
  fontWeight: 600,
  padding: "12px 14px",
  borderRadius: 10,
  border: "none",
  background: "linear-gradient(95deg, var(--brand-deep) 0%, var(--brand-teal) 100%)",
  color: "white",
  cursor: "pointer",
  letterSpacing: 0.3,
  textDecoration: "none",
  textAlign: "center",
  display: "block",
};

const secondaryButtonStyle: React.CSSProperties = {
  font: "inherit",
  fontWeight: 600,
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid var(--border-subtle)",
  background: "transparent",
  color: "var(--text)",
  cursor: "pointer",
};
